/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import Text from 'src/components/ts-components/Text'
import { useRegistrationSetupContext } from '../../context'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import SelectMultiInput from 'src/components/ts-components/SelectMultiInput'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link } from 'react-router-dom'
import { useMemo } from 'react'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const Payments = () => {
  const {
    state,
    setState,
    availableTaxes,
    registrationTypes,
    apiAccount,
    registration,
  } = useRegistrationSetupContext()

  // For Canlan, we must consider the payment provider type from the registration
  // and not the apiAccount, since they dont have this set at the account level.
  // For the other customers, we must consider the payment provider type from the apiAccount,
  // since they have this set at the account level and ensure they dont set the payment provider
  // that is not configured in the account settings.
  const paymentProviderOptions = useMemo(() => {
    if (isCanlan) {
      return registration?.payment_provider_type
        ? [
            {
              label: registration?.payment_provider_type?.name,
              value: registration?.payment_provider_type?.id,
            },
          ]
        : []
    }
    return apiAccount?.payment_provider_type
      ? [
          {
            label: apiAccount?.payment_provider_type?.name,
            value: apiAccount?.payment_provider_type?.id,
          },
        ]
      : []
  }, [apiAccount?.payment_provider_type, registration?.payment_provider_type])

  return (
    <div
      css={css`
        display: flex;
        padding: 16px;
        gap: 16px;
        flex-direction: column;
        align-items: flex-end;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
          align-self: stretch;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Payments
        </Text>
      </div>
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: 16px;
          align-self: stretch;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <SelectInput
            label='Payment Provider'
            options={paymentProviderOptions}
            value={state.payment_provider || ''}
            onChange={(value) => {
              setState({
                ...state,
                payment_provider: value,
              })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='payment_provider'
            notFoundContent={
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background: transparent;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  Set Up Your Payment Provider In{' '}
                  <Link
                    to={`${ROUTES.GENERAL_SETTINGS}?origin=payment_provider`}
                    target='_blank'
                    css={css`
                      color: ${colors.PRIMARY};
                    `}
                  >
                    Account Settings
                  </Link>
                </Text>
              </div>
            }
          />
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            flex: 1 0 0;
          `}
        >
          <SelectInput
            label='Currency'
            options={
              registrationTypes?.currency
                ?.sort((a, b) => {
                  if (a.name === 'USD') {
                    return -1
                  }
                  if (b.name === 'USD') {
                    return 1
                  }
                  if (a.name === 'CAD') {
                    return -1
                  }
                  if (b.name === 'CAD') {
                    return 1
                  }
                  return 0
                })
                ?.map((currency) => ({
                  label: currency.name,
                  value: currency.id,
                })) || []
            }
            value={state.currency}
            onChange={(value) => {
              setState({
                ...state,
                currency: value,
              })
            }}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            disabled={false}
            name='currency'
          />
        </div>
      </div>
      <SelectMultiInput
        label='Taxes'
        options={
          availableTaxes?.map((tax) => ({
            label: `${tax.name} (${tax.tax_rate}%)`,
            value: tax.id,
          })) || []
        }
        value={state.taxes}
        onChange={(value) => {
          setState({
            ...state,
            taxes: value,
          })
        }}
        fieldErrors={state.fieldErrors}
        customError={state.customError}
        name='taxes'
      />
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          align-self: stretch;
          width: 100%;
        `}
      >
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style={'align-self: flex-end;'}
        >
          Taxes And Your Payment Provider Can Be Configured In The{' '}
          <Link
            to={`${ROUTES.GENERAL_SETTINGS}?origin=payment_provider`}
            target='_blank'
            css={css`
              color: ${colors.PRIMARY};
            `}
          >
            Account Settings.
          </Link>
        </Text>
      </div>
    </div>
  )
}

export default Payments
